/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react';
import Box from '@material-ui/core/Box';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Crumb, GroupCategoryEntity} from '@ion/core/src/types/entities';
import {GlobalGroupProps} from '@ion/core/src/types/general';
import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import TextField from '@ion/dls/src/components/textfield/TextField';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {GroupChooser} from '@ion/dls/src/editors/GroupChooser';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {EmptySelectLabels} from '@ion/dls/src/types/types';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import LandmarkCategoryChooser from '../../editors/LandmarkCategoryChooser';
import {GENERATE_EVENTS_VALUE, NONE_TRIGGER_EVENT} from './constants';
import {textFieldStyles} from './SearchAddress';

export type SaveSettingsProps = GlobalGroupProps & {
  assetCategories?: string[];
  finalDrawingDetails?: any;
  geofenceSave?: Function;
  selectCrumbs?: Function;
  crumbs?: Crumb[];
  geofencePanel?: Function;
  savingGeofence?: boolean;
  geoFencesGraphicsLayerRef?: {
    current: null | __esri.Layer;
  };
  sketchViewModelRef?: {
    current: null | __esri.Layer;
  };
  selectedMapPointRef?: {
    current: null | __esri.Layer;
  };
  map: __esri.Map;
  editGeofenceGraphic?: __esri.Graphic;
  mapClickHandler?: any;
  handleBack?: Function;
  closeGeoFencePanel?: Function;
}

type GeoFenceAttributes = {
  landmarkName: string;
  landmarkCategory?: string;
  geofenceType: string;
  landmarkRestricted: string;
};

const useStyles = makeStyles((theme) => ({
  container : {
    maxWidth   : ({isPhone}: {isPhone: boolean}) => isPhone ? '100%' : 285,
    maxHeight  : 600,
    margin     : 10,
    alignItems : 'center'
  },
  actionButtonContainer : {
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'space-between',
    alignItems     : 'center'
  },
  cancelButton : {
    textAlign : 'center',
    width     : '50%'
  },
  saveGeofence : {
    width : '90%'
  },
  geofenceDescription : {
    marginBottom : '10px'
  },
  geofenceRestrictWrapper : {
    padding        : '10px 20px',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between'
  },
  closeIcon : {
    cursor : 'pointer'
  },
  dropdownWrapper : {
    margin : `${theme.spacing(1)}px 0`,
    width  : '100%'
  },
  geofenceWrapper : {
    margin : `${theme.spacing(2)}px 0`,
    width  : '100%'
  },
  inputStyle : {
    paddingLeft : '0px !important',
    zIndex      : dlsZIndex('saveSettingsInputStyle')
  },
  editGeoFenceHeader : {
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'space-between',
    alignItems     : 'center',
    width          : ({isPhone}: {isPhone: boolean}) => isPhone ? '100%' : 'inherit',
    margin         : '-10px -10px 10px -10px',
    height         : '26px',
    padding        : 5,
    background     : dlsColor('grey10'),
    borderRadius   : '4px 4px 0px 0px'
  },
  geofenceHeader : {
    height        : '16px',
    left          : '5px',
    top           : '5px',
    fontFamily    : 'Roboto !important',
    fontStyle     : 'normal',
    fontWeight    : 700,
    fontSize      : '12px !important',
    lineHeight    : '16px !important',
    letterSpacing : '0.5px !important',
    color         : dlsColor('white'),
    marginLeft    : 6
  },
  ...textFieldStyles
}));

const textFieldStyleOverrides = () => ({
  root : {
    width : '100%'
  }
});

const SaveSettings = ({
  finalDrawingDetails,
  geofenceSave,
  selectCrumbs,
  crumbs,
  globalGroup,
  accountMap,
  savingGeofence,
  geoFencesGraphicsLayerRef,
  sketchViewModelRef,
  selectedMapPointRef,
  map,
  editGeofenceGraphic,
  mapClickHandler,
  handleBack,
  closeGeoFencePanel
}: SaveSettingsProps) => {
  const {isPhone}                               = useMedia();
  const classes                                 = useStyles({isPhone});

  const [geofenceName, setGeofenceName]         = useState<string>('');
  const [description, setDescription]           = useState<string>('');
  const [categoryValue, setCategoryValue]       = useState<any>('');
  const [restrictedValue, setRestrictedValue]   = useState(false);
  const [generateEnterExitEvents,
    setGenerateEnterExitEvents]                 = useState(false);
  const [saveEnabled, setSaveEnabled]           = useState(false);
  const [groupValue, setGroupValue]             = useState(null);
  const [groupsLoading, setGroupsLoading]       = useState(false);

  const translations                            = translate('maps:geofences', {returnObjects: true});

  const noneValue = useMemo(() => capitalizeFirstLetter(EmptySelectLabels.none), []);
  const attributes = useMemo(() => editGeofenceGraphic?.attributes || {}, [editGeofenceGraphic]);

  const groupChooserObj = attributes?.landmarkAccountId
    ? accountMap?.[attributes?.landmarkAccountId]
    : globalGroup;

  useEffect(() => {
    setGeofenceName(attributes?.landmarkName || '');
    setDescription(attributes?.description || '');
    setCategoryValue(attributes?.landmarkCategory || '');
    setRestrictedValue(attributes?.landmarkRestricted === 'True' || false);
    setGenerateEnterExitEvents(attributes?.geofenceType === GENERATE_EVENTS_VALUE);
    setSaveEnabled(attributes?.landmarkName?.length || false);
    setGroupValue(groupChooserObj || '');
  }, [attributes]);

  function handleGeofenceName({target}) {
    if (target.value.length === 0) {
      setSaveEnabled(false);
    } else {
      setSaveEnabled(true);
    }
    setGeofenceName(target.value);
  }

  function handleDescription({target}) {
    setDescription(target.value);
  };

  const handleGlobalGroupChange = useCallback(value => {
    setGroupValue(value);
  }, []);

  const handleCategory = useCallback(value => {
    setCategoryValue(value);
  }, []);

  function handleChangeRestrict(e) {
    setRestrictedValue(!restrictedValue);
  }

  function handleGenerateEnterExitEvents({target}: ChangeEvent<HTMLInputElement>) {
    setGenerateEnterExitEvents(target.checked);
  }

  // To Cancel the current drawing when click on cancel
  function cancelDrawing() {
    handleBack?.();
    selectCrumbs(0, crumbs);
  }

  // To save the created Geofence
  async function saveGeofence({isDelete = false}) {
    const attrs: GeoFenceAttributes = {
      ...attributes,
      landmarkName       : geofenceName,
      description        : description,
      geofenceType       : generateEnterExitEvents ? GENERATE_EVENTS_VALUE : NONE_TRIGGER_EVENT,
      landmarkCategory   : categoryValue !== noneValue ? categoryValue : null,
      landmarkRestricted : capitalizeFirstLetter(restrictedValue.toString()),
      landmarkAccount    : groupValue?.title,
      landmarkAccountId  : entityId(groupValue)
    };
    let rings = [finalDrawingDetails];
    if (!finalDrawingDetails && editGeofenceGraphic?.geometry?.rings) {
      rings = [editGeofenceGraphic.geometry.rings[0]];
    }
    geofenceSave({
      isUpdate     : !isDelete && (!!editGeofenceGraphic ?? false),
      landmarkName : attributes?.landmarkName,
      geofence     : {attrs, rings},
      isDelete
    });
    // clear layers so user can create a new fence
    map.remove(geoFencesGraphicsLayerRef.current);
    map.remove(sketchViewModelRef.current);
    map.remove(selectedMapPointRef?.current);
    geoFencesGraphicsLayerRef.current = null;
    sketchViewModelRef.current = null;
    selectedMapPointRef.current = null;
    mapClickHandler?.remove();
    if (editGeofenceGraphic) {
      cancelDrawing();
    }
    closeGeoFencePanel?.();
  }

  const closeSaveSettings = useCallback(() => {
    cancelDrawing();
    closeGeoFencePanel?.();
  }, [cancelDrawing, closeGeoFencePanel]);

  return (
    <div className={classes.container}>
      {editGeofenceGraphic && (
        <Box className={classes.editGeoFenceHeader}>
          <Box flexGrow={1}>
            <Box>
              <HeaderTypography variant='h4' className={classes.geofenceHeader}>
                {translations.editGeofence}
              </HeaderTypography>
            </Box>
          </Box>
          <Box className={classes.closeIcon} onClick={closeSaveSettings}>
            <MaterialExitCloseIcon />
          </Box>
        </Box>
      )}
      <form noValidate autoComplete='off'>
        <TextField label={translations.geofenceName}
          className={classes.geofenceDescription}
          fullWidth
          styleOverrides={textFieldStyleOverrides}
          value={geofenceName}
          InputProps={{
            classes : {
              underline : classes.underline,
              input     : classes.inputStyle
            }
          }}
          onChange={handleGeofenceName} />
        <TextField label={translations.description}
          className={classes.geofenceDescription}
          fullWidth
          styleOverrides={textFieldStyleOverrides}
          value={description}
          InputProps={{
            classes : {
              underline : classes.underline,
              input     : classes.inputStyle
            }
          }}
          inputProps={{
            maxLength : 500
          }}
          onChange={handleDescription} />
        <div className={classes.geofenceWrapper}>
          <GroupChooser required
            account={groupValue || globalGroup}
            onLoad={setGroupsLoading}
            onChange={handleGlobalGroupChange}
            defaultValue={groupChooserObj || globalGroup} />
        </div>
        <div className={classes.dropdownWrapper}>
          <LandmarkCategoryChooser entity={GroupCategoryEntity.landmark}
            includeGroupName={false}
            defaultValue={categoryValue}
            isLoading={groupsLoading}
            onChange={handleCategory} />
        </div>
        <div className={classes.geofenceRestrictWrapper}>
          <CheckBox label={translations.generateEnterExitEvents}
            onChange={handleGenerateEnterExitEvents}
            value={generateEnterExitEvents} checked={generateEnterExitEvents} />
          <Tooltip title={translations.generateEnterExitEventsInfo}
            arrow>
            <span>
              <InfoIcon />
            </span>
          </Tooltip>
        </div>
        <div className={classes.geofenceRestrictWrapper}>
          <CheckBox label={translations.restricted}
            onClick={handleChangeRestrict}
            value={restrictedValue} checked={restrictedValue} />
          <Tooltip title={translations.restrictedInfo}
            arrow>
            <span>
              <InfoIcon />
            </span>
          </Tooltip>
        </div>
        {!editGeofenceGraphic && (
          <div className={classes.actionButtonContainer}>
            <div className={classes.cancelButton}>
              <TertiaryButton noUnderline
                onClick={function() {
                  cancelDrawing();
                }}>
                {translations.cancel}
              </TertiaryButton>
            </div>
            <PrimaryButton disabled={!saveEnabled || savingGeofence}
              className={classes.saveGeofence}
              fullWidth
              onClick={saveGeofence}>
              {savingGeofence
                ? translations.savingGeofence
                : translations.saveGeofence}
            </PrimaryButton>
          </div>
        )}
        {editGeofenceGraphic && (
          <Box className={classes.actionButtonContainer}>
            <TertiaryButton noUnderline
              className={classes.cancelButton}
              onClick={function() {
                saveGeofence({
                  isDelete : true
                });
              }}>
              {translations.delete}
            </TertiaryButton>
            <PrimaryButton disabled={!saveEnabled || savingGeofence}
              className={classes.saveGeofence}
              fullWidth={true}
              onClick={saveGeofence}>
              {translations.save}
            </PrimaryButton>
          </Box>
        )}
      </form>
    </div>
  );
};

export default SaveSettings;
